import { getStateOptions } from '@sequencehq/api/utils/commonEnums'
import type { CountriesAlpha2 } from '@sequencehq/api/commonEnums'
import { getHumanReadableInvoiceNumber } from 'Invoices/utils/getHumanReadableInvoiceNumber'
import {
  ConfirmedInvoiceFields,
  InvoiceReviewFormValues,
  InvoiceSnapshot,
  InvoiceUpdateReviewInput,
  InvoiceUpdateReviewViewModel
} from './types'

/**
 * Maps a raw task input to the view model the review components consume: the
 * invoice identity, the agent summary, the forwarded source email, and the four
 * proposed field values (read from the `after` snapshot) that seed the form.
 */
export const mapInputToViewModel = (
  input: InvoiceUpdateReviewInput
): InvoiceUpdateReviewViewModel => ({
  invoiceId: input.invoiceId,
  summary: input.summary,
  source: input.source,
  invoice: {
    invoiceNumber: input.after.invoiceNumber,
    status: input.after.status,
    customerLegalCompanyName: input.after.customerLegalCompanyName,
    customerId: input.after.customerId
  },
  proposed: snapshotToFormValues(input.after)
})

/**
 * The seed an address-less invoice starts from. Every field is blank, so the
 * form's own validators report each one as required and the operator has to
 * complete the address before the review can be confirmed.
 */
const EMPTY_ADDRESS = {
  line1: '',
  town: '',
  postcode: '',
  country: '' as CountriesAlpha2
}

/**
 * Extracts the four patchable fields from an invoice snapshot into the form's
 * shape. The three free-text fields use empty string (not `null`) while editing.
 * An invoice whose customer has no address arrives with none, and the form
 * edits an address in place, so it starts from a blank one rather than null.
 */
export const snapshotToFormValues = (
  snapshot: InvoiceSnapshot
): InvoiceReviewFormValues => ({
  customerBillingAddress: snapshot.customerBillingAddress ?? EMPTY_ADDRESS,
  purchaseOrderNumber: snapshot.purchaseOrderNumber ?? '',
  reference: snapshot.reference ?? '',
  memo: snapshot.memo ?? ''
})

/**
 * Builds the `invoice` record submitted on confirm from the form values: the
 * billing address is sent as-is, and each free-text field is trimmed and sent as
 * `null` when empty so the backend clears it rather than storing a blank string.
 */
export const formValuesToConfirmedFields = (
  values: InvoiceReviewFormValues
): ConfirmedInvoiceFields => ({
  customerBillingAddress: values.customerBillingAddress,
  purchaseOrderNumber: emptyToNull(values.purchaseOrderNumber),
  reference: emptyToNull(values.reference),
  memo: emptyToNull(values.memo)
})

/**
 * Title shown in the inbox row and review header, e.g. "Update invoice INV-0018".
 * Reuses the shared human-readable invoice number so a draft with no number reads
 * consistently with the rest of the app.
 */
export const getInvoiceReviewTitle = (
  invoice: Pick<
    InvoiceUpdateReviewViewModel['invoice'],
    'invoiceNumber' | 'status'
  >
): string => `Update ${getInvoiceLabel(invoice)}`

/**
 * The invoice referred to in prose, e.g. "invoice INV-0018" — used in the
 * confirm/dismiss dialog copy and toasts so they read consistently.
 */
export const getInvoiceLabel = (
  invoice: Pick<
    InvoiceUpdateReviewViewModel['invoice'],
    'invoiceNumber' | 'status'
  >
): string =>
  `invoice ${getHumanReadableInvoiceNumber({
    invoiceNumber: invoice.invoiceNumber,
    invoiceStatus: invoice.status
  })}`

/** Validation errors for the billing-address fields, keyed by address field. */
export type InvoiceReviewFormErrors = Partial<
  Record<'line1' | 'town' | 'postcode' | 'country' | 'state', string>
>

/**
 * Validates the editable billing address: line 1, city, postcode and country are
 * always required, and a state is required when the selected country has states
 * to choose from (US/Canada). The free-text fields (PO number, reference, memo)
 * are optional and never invalid. Pure function.
 */
export const getInvoiceReviewFormErrors = (
  values: InvoiceReviewFormValues
): InvoiceReviewFormErrors => {
  const address = values.customerBillingAddress
  const errors: InvoiceReviewFormErrors = {}

  if (address.line1.trim() === '') errors.line1 = 'Address line 1 is required'
  if (address.town.trim() === '') errors.town = 'City/town is required'
  if (address.postcode.trim() === '')
    errors.postcode = 'Postal code is required'
  if (!address.country) errors.country = 'Country is required'

  const countryHasStates = getStateOptions(address.country).length > 0
  if (countryHasStates && !address.state) errors.state = 'State is required'

  return errors
}

export const isInvoiceReviewFormValid = (
  values: InvoiceReviewFormValues
): boolean => Object.keys(getInvoiceReviewFormErrors(values)).length === 0

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
